import { motion } from "framer-motion";
import { 
  Play, 
  SkipForward, 
  SkipBack, 
  Volume2, 
  Maximize, 
  CheckCircle2,
  Clock,
  Keyboard,
  Bookmark,
  RotateCcw,
} from "lucide-react";
import { Container, SectionHeading, SectionSubheading } from "./shared";

function VideoPlayerMockup() {
  const chapters = [
    { title: "Introduction", time: "0:00", progress: 100 },
    { title: "Setting Up", time: "3:24", progress: 100 },
    { title: "Core Concepts", time: "8:15", progress: 45 },
    { title: "Building Features", time: "15:30", progress: 0 },
    { title: "Advanced Topics", time: "24:00", progress: 0 },
    { title: "Deployment", time: "32:45", progress: 0 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      viewport={{ once: true }}
      className="bg-neutral-900 rounded-2xl overflow-hidden shadow-2xl border border-neutral-800"
    >
      {/* Video Area */}
      <div className="relative aspect-video bg-neutral-900">
        {/* Thumbnail */}
        <img
          src="https://assets.uselint.com/core/galactic.jpg"
          alt="Video thumbnail"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        {/* Play button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer group">
            <Play className="w-8 h-8 text-white ml-1 group-hover:scale-110 transition-transform" />
          </div>
        </div>
        
        {/* Current chapter indicator */}
        <div className="absolute top-4 left-4 bg-black/60 backdrop-blur rounded-lg px-3 py-1.5 flex items-center gap-2">
          <Bookmark className="w-4 h-4 text-emerald-400" />
          <span className="text-white text-sm font-medium">Core Concepts</span>
        </div>
        
        {/* Time indicator */}
        <div className="absolute top-4 right-4 bg-black/60 backdrop-blur rounded-lg px-3 py-1.5">
          <span className="text-white text-sm font-mono">12:34 / 45:00</span>
        </div>
        
        {/* Progress bar with chapters */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-4">
          <div className="relative h-2 bg-white/20 rounded-full overflow-hidden">
            {/* Chapter markers */}
            {chapters.map((chapter, i) => {
              const position = (i / (chapters.length - 1)) * 100;
              return (
                <div
                  key={i}
                  className="absolute top-0 bottom-0 w-0.5 bg-white/40"
                  style={{ left: `${position}%` }}
                />
              );
            })}
            {/* Progress */}
            <div className="h-full w-[28%] bg-emerald-500 rounded-full" />
            {/* Scrubber */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg"
              style={{ left: '28%', transform: 'translateX(-50%) translateY(-50%)' }}
            />
          </div>
        </div>
        
        {/* Controls overlay */}
        <div className="absolute bottom-16 left-0 right-0 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <SkipBack className="w-5 h-5 text-white" />
              </button>
              <button className="w-12 h-12 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform">
                <Play className="w-5 h-5 text-neutral-900 ml-0.5" />
              </button>
              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <SkipForward className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <Volume2 className="w-5 h-5 text-white" />
              </button>
              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <Maximize className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Chapters List */}
      <div className="p-4 border-t border-neutral-800">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-white">Chapters</h4>
          <span className="text-xs text-neutral-400">2 of 6 completed</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {chapters.map((chapter, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.3 }}
              viewport={{ once: true }}
              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                i === 2 
                  ? 'bg-emerald-500/20 border border-emerald-500/30' 
                  : 'bg-neutral-800 hover:bg-neutral-700'
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                chapter.progress === 100 
                  ? 'bg-emerald-500 text-white' 
                  : i === 2 
                    ? 'bg-emerald-500/30 text-emerald-400'
                    : 'bg-neutral-700 text-neutral-400'
              }`}>
                {chapter.progress === 100 ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  i + 1
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-medium truncate ${i === 2 ? 'text-emerald-400' : 'text-neutral-300'}`}>
                  {chapter.title}
                </div>
                <div className="text-[10px] text-neutral-500">{chapter.time}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

const features = [
  {
    icon: Bookmark,
    title: "Chapter Navigation",
    description: "Jump to any section instantly with clickable chapters",
  },
  {
    icon: RotateCcw,
    title: "Resume Playback",
    description: "Pick up exactly where you left off, every time",
  },
  {
    icon: Keyboard,
    title: "Keyboard Shortcuts",
    description: "Space, arrows, F, M - control playback like a pro",
  },
  {
    icon: Clock,
    title: "Progress Tracking",
    description: "Automatic progress saves as students watch",
  },
];

export function VideoPlayerShowcase() {
  return (
    <section className="py-16 md:py-24 lg:py-32 bg-background">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <SectionHeading className="mb-6">
              Professional Video Player.{" "}
              <span className="text-emerald-500">Built for Learning.</span>
            </SectionHeading>
            
            <SectionSubheading className="mb-8">
              Your students get a distraction-free viewing experience with chapter navigation, 
              progress tracking, and keyboard shortcuts — no third-party embeds needed.
            </SectionSubheading>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.4 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{feature.title}</h4>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Video Player Mockup */}
          <div className="order-first lg:order-last">
            <VideoPlayerMockup />
          </div>
        </div>
      </Container>
    </section>
  );
}
